import { FC, lazy } from 'react';
import { useParams } from 'react-router-dom';
import {
  AuthorizationServiceRequest,
  useAuthorizationService,
} from 'src/apis/authorization-service/use-authorization-service';
import { usePermissions } from 'src/apis/authorization-service/use-permissions';
import { useUserPermissionGroups } from 'src/apis/authorization-service/use-user-authorization-data';
import { PageSpinner } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { OrganisationServiceRequest } from 'src/pages/settings/organisation/use-organisation-service';
import {
  UserDetailsRequest,
  useUserDetails,
} from 'src/pages/settings/organisation/users/details/use-user-details';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(
  () => import('src/pages/settings/organisation/users/details/page')
);

type Props = {
  organisationService: Successful<OrganisationServiceRequest>;
};

export const UserDetails: FC<Props> = ({ organisationService }) => {
  if (!organisationService.response.data._links.getUserDetails)
    throw new Forbidden();

  const { userId } = useParams<{ userId: string }>();
  const userDetails = useUserDetails({
    url: organisationService.response.data._links.getUserDetails.href,
    userId,
  });

  const authorizationService = useAuthorizationService();

  if (!userDetails.response || authorizationService?.response === null)
    return <PageSpinner />;

  // handle this intermediate state when authorization-service is already on ENT, but
  // the user authorization data is not working, yet.
  // in the future we can merge these two components again.
  if (
    authorizationService?.response.data._links.permissions &&
    (authorizationService.response.data._links.manageUserAssignments ||
      authorizationService.response.data._links.manageUserAssignmentsV2)
  ) {
    return (
      <LoadAuthorizationData
        userDetails={userDetails}
        organisationService={organisationService}
        authorizationService={authorizationService}
      />
    );
  } else {
    return (
      <Page
        userDetails={userDetails}
        organisationService={organisationService}
      />
    );
  }
};

const LoadAuthorizationData: FC<{
  userDetails: Successful<UserDetailsRequest>;
  organisationService: Successful<OrganisationServiceRequest>;
  authorizationService: Successful<AuthorizationServiceRequest>;
}> = ({ organisationService, userDetails, authorizationService }) => {
  const { userId } = useParams<{ userId: string }>();

  const authorizationData = useUserPermissionGroups({
    authorizationService,
    userId,
  });
  const permissions = usePermissions({ authorizationService });

  if (authorizationData?.response === null || permissions?.response === null)
    return <PageSpinner />;

  return (
    <Page
      userDetails={userDetails}
      organisationService={organisationService}
      authorizationService={authorizationService}
      permissions={permissions}
      authorizationData={authorizationData}
    />
  );
};
